'use client'

import React, { createContext, useContext, ReactNode, useEffect } from 'react'
import { FormData } from '@/components/modals/interface'
import { useSelector } from 'react-redux'
import { RootState } from 'features/store'

const initialState = {
    listing_id: undefined,
    businessType: '',
    membership_type: '',
    companyName: '',
    firstName: '',
    lastName: '',
    password: '',
    description: '',
    streetAddress: '',
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    zip: '',
    daysOfOperation: [],
    ingredients: [],
    vendors: [],
    practices: [],
    sellingLocations: [],
    salesChannels: [],
    openingTime: '',
    closingTime: '',
    website: '',
    tagLine: '',
    socialLinks: [
      { id: 1, platform: 'Facebook', value: '' },
      { id: 2, platform: 'Instagram', value: '' },
      { id: 3, platform: 'Twitter', value: '' },
    ],
    socialMedia: [],
    contactEmail: '',
    contactPhone: '',
    referralId: '',
    photo: null,
    file: null,
    result: null,
    selectedPlanName: '',
    stripeProductId: '',
    stripe_sub_id: '',
    stripe_price_id: '',
    stripe_customer_id: '',
    subscription_status: '',
    subscription_expiry: null,
    payment_type: '',
    tax_exemption: false,
    referral_code: undefined,
    balance: 0,
    selectedPractices: [],
    hours: [],
    operatingHours: [],
    locationName: '',
    locationAddress: '',
    additionalNotes: ''
}

type FormDataContextType = {
    formData: FormData
    setFormData: React.Dispatch<React.SetStateAction<FormData>>
    resetFormData: () => void
}

const FormDataContext = createContext<FormDataContextType | undefined>(undefined)

export const FormDataProvider = ({
    children,
    initialData,
}: {
    children: ReactNode
    // Seeds formData before first render -- unlike patching via setFormData in a useEffect,
    // this is guaranteed to be present the first time a consuming child (e.g. Stripe's
    // AddressElement, which only reads defaultValues once at mount) reads formData.
    initialData?: Partial<FormData>
}) => {
    const [formData, setFormData] = React.useState<FormData>(() => ({
        ...initialState,
        ...initialData,
    }))
    const upgradeListing = useSelector((state: RootState) => state.listing.listingUpgrader)

    useEffect(() => {
        if(upgradeListing != null) {
            setFormData((prevFormData) => ({
              ...prevFormData,
              listing_id: upgradeListing.listing_id,
              businessType: upgradeListing.user_type,
              membership_type: upgradeListing.membership_type,
              companyName: upgradeListing.name,
              firstName: upgradeListing.first_name,
              lastName: upgradeListing.last_name,
              email: upgradeListing.email || upgradeListing.publicEmail,
              phoneNumber: upgradeListing.phone || upgradeListing.publicPhone,
              ingredients: upgradeListing.ingredients || [],
              city: upgradeListing.city,
              state: upgradeListing.state,
              latitude: upgradeListing.latitude,
              longitude: upgradeListing.longitude,
              briefSummary: upgradeListing.tagLine,
              description: upgradeListing.description,
              streetAddress: upgradeListing.address,
              zip: upgradeListing.zipcode,
              vendors: upgradeListing.vendors || [],
              referral_code: upgradeListing?.user?.referral_code
            }))
        }
    }, [setFormData, upgradeListing])


    const resetFormData = () => setFormData(initialState)

    return (
        <FormDataContext.Provider value={{ formData, setFormData, resetFormData }}>
            {children}
        </FormDataContext.Provider>
    )
}

export const useFormDataContext = (): FormDataContextType => {
    const context = useContext(FormDataContext)
    if (!context) {
        throw new Error('useFormDataContext must be used within a FormDataProvider')
    }
    return context
}
